// src/pages/school/fees/data/feesAuth.js
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../../../services/firebase";

/** Hash helper (same scheme you used elsewhere) */
export async function sha256Hex(str) {
  const enc = new TextEncoder().encode(str);
  const hashBuf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(hashBuf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Storage key = per-school, per-admin */
export const feeKey = (schoolId, uid) => `feeAuth:${schoolId}:${uid}`;

/** TTL for remembered unlock (in hours) */
const TTL_HOURS = 24;

function nowMs() {
  return Date.now();
}

/** Clear both session & local */
export function clearFeeAuth(schoolId, uid) {
  const k = feeKey(schoolId, uid);
  try {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  } catch {}
}

/** Check remembered unlock */
export function isFeeUnlocked(schoolId, uid) {
  const k = feeKey(schoolId, uid);
  try {
    const raw =
      localStorage.getItem(k) ||
      sessionStorage.getItem(k);
    if (!raw) return false;
    const obj = JSON.parse(raw);
    if (!obj?.at) return false;
    const ageHrs = (nowMs() - Number(obj.at)) / (1000 * 60 * 60);
    if (ageHrs > TTL_HOURS) {
      clearFeeAuth(schoolId, uid);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Persist unlock token (session or local) */
export function setFeeUnlocked(schoolId, uid, { remember }) {
  const k = feeKey(schoolId, uid);
  const payload = JSON.stringify({ at: nowMs() });
  try {
    if (remember) localStorage.setItem(k, payload);
    else sessionStorage.setItem(k, payload);
  } catch {}
}

/** Read fee config doc: /schools/{schoolId}/config/fees */
export async function fetchFeeConfig(schoolId) {
  const ref = doc(db, "schools", schoolId, "config", "fees");
  const snap = await getDoc(ref);
  return snap.exists() ? snap.data() : null; // { pinHash?: string, hint?: string }
}

/**
 * Verify the PIN.
 * - If no pinHash is configured, treat as unlocked.
 * - Otherwise, compare sha256Hex(`${pin}::${schoolId}`) with stored hash.
 */
export async function verifyFeePin({ schoolId, pin }) {
  const cfg = await fetchFeeConfig(schoolId);
  if (!cfg?.pinHash) {
    return { ok: true, reason: "no-pin-set", hint: cfg?.hint || "" };
  }
  const inputHash = await sha256Hex(`${pin}::${schoolId}`);
  const ok = inputHash === cfg.pinHash;
  return { ok, hint: cfg?.hint || "" };
}
